import { Trans, useLingui } from '@lingui/react/macro'
import dayjs, { type Dayjs } from 'dayjs'
import { Button } from 'designSystem/components/Button'
import { DatePicker } from 'designSystem/components/DatePicker'
import { Field, FieldError, FieldLabel } from 'designSystem/components/Field'
import { Input } from 'designSystem/components/Input'
import { Text } from 'designSystem/components/Text'
import { useId, useState } from 'react'
import styled from 'styled-components'

import { withTime } from './customRangeTime'

const CustomRangeForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-top: 0.0625rem solid var(--border);
  margin-inline: -1rem;
  padding: 0.75rem 1rem 0;
`

const DateTimeRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: end;
  gap: 0.75rem;
`

export const CustomRangePicker = ({
  fromTime,
  toTime,
  onApply,
}: {
  fromTime: Dayjs
  toTime: Dayjs
  onApply: (dates: [Dayjs, Dayjs]) => void
}) => {
  const { t } = useLingui()
  const id = useId()
  const [from, setFrom] = useState(fromTime)
  const [to, setTo] = useState(toTime)
  const [startTime, setStartTime] = useState(fromTime.format('HH:mm'))
  const [endTime, setEndTime] = useState(toTime.format('HH:mm'))
  const start = withTime(from, startTime)
  const end = withTime(to, endTime)
  const valid = Boolean(
    startTime &&
    endTime &&
    start?.isValid() &&
    end?.isValid() &&
    end.isAfter(start)
  )

  return (
    <CustomRangeForm>
      <Text variant='overline' color='muted'>
        <Trans>Custom Range</Trans>
      </Text>
      <DateTimeRow>
        <DatePicker
          label={t`Start date`}
          width='full'
          date={from.toDate()}
          onDateChange={(date) => {
            if (date) setFrom(dayjs(date))
          }}
        />
        <Field>
          <FieldLabel htmlFor={`${id}-start`}>
            <Trans>Start Time</Trans>
          </FieldLabel>
          <Input
            inputSize='compact'
            id={`${id}-start`}
            type='time'
            required
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
          />
        </Field>
      </DateTimeRow>
      <DateTimeRow>
        <DatePicker
          label={t`End date`}
          width='full'
          date={to.toDate()}
          onDateChange={(date) => {
            if (date) setTo(dayjs(date))
          }}
        />
        <Field>
          <FieldLabel htmlFor={`${id}-end`}>
            <Trans>End Time</Trans>
          </FieldLabel>
          <Input
            inputSize='compact'
            id={`${id}-end`}
            type='time'
            required
            value={endTime}
            onChange={(event) => setEndTime(event.target.value)}
          />
        </Field>
      </DateTimeRow>
      {!valid && (
        <FieldError>
          <Trans>End time must be after start time.</Trans>
        </FieldError>
      )}
      <Button
        type='button'
        disabled={!valid}
        onClick={() => {
          if (valid && start && end) onApply([start, end])
        }}>
        <Trans>Apply</Trans>
      </Button>
    </CustomRangeForm>
  )
}
